import React, { useState } from 'react';
import { X, Link2, Check, Loader2, Eye, UserPlus } from 'lucide-react';
import { createMediaShare, mediaShareUrl, type ShareMode } from '../../services/mediaShareService';
import { TYPE } from '../../constants/typeScale';

interface Props {
  open: boolean;
  conversationId: string;
  messageId: string;
  onClose: () => void;
}

/**
 * Share a generated media asset as a public link. Two modes: "Share only" (the viewer just sees
 * the content) and "Share & invite" (the viewer also gets a join-the-chat CTA). Picking a mode
 * creates/updates the share and COPIES the wisetree.ai/m/{id} link straight to the clipboard,
 * confirming with a "Copied" status — the user never has to find + copy it. Only if the clipboard
 * is blocked do we fall back to showing the link for a manual copy.
 */
const ShareMediaModal: React.FC<Props> = ({ open, conversationId, messageId, onClose }) => {
  const [mode, setMode] = useState<ShareMode | null>(null);
  const [busy, setBusy] = useState<ShareMode | null>(null);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const choose = async (m: ShareMode) => {
    setBusy(m); setError(''); setCopied(false); setLink('');
    try {
      const { mediaId } = await createMediaShare(conversationId, messageId, m);
      const url = mediaShareUrl(mediaId);
      setMode(m); setLink(url);
      // Copy straight away — the whole point of sharing is to get the link onto the clipboard.
      try { await navigator.clipboard.writeText(url); setCopied(true); }
      catch { setCopied(false); } // clipboard blocked → fall back to the manual copy box below
    } catch (e: any) {
      setError(e?.message || 'Could not create the share link');
    } finally { setBusy(null); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); }
    catch { /* clipboard blocked */ }
  };

  const Option: React.FC<{ m: ShareMode; icon: React.ReactNode; title: string; desc: string }> = ({ m, icon, title, desc }) => (
    <button type="button" onClick={() => choose(m)} disabled={!!busy}
      className={`w-full text-left rounded-xl border p-3 transition-colors disabled:opacity-50 ${mode === m
        ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800'
        : 'border-secondary-200 dark:border-secondary-600 hover:border-secondary-400'}`}>
      <div className="flex items-center gap-2">
        <span className="text-secondary-700 dark:text-secondary-200">{busy === m ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}</span>
        <span className={`${TYPE.body} font-medium text-secondary-900 dark:text-white`}>{title}</span>
      </div>
      <p className={`mt-1 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>{desc}</p>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-600 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-600">
          <h2 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Share this media</h2>
          <button onClick={onClose} aria-label="Close" title="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-2.5">
          <Option m="view" icon={<Eye className="w-4 h-4" />} title="Share only"
            desc="Anyone with the link can view this content on Wisetree." />
          <Option m="invite" icon={<UserPlus className="w-4 h-4" />} title="Share & invite"
            desc="They can view it and join this chat group to create their own." />

          {error && <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

          {/* Happy path: the link is already on the clipboard — just confirm it. */}
          {link && copied && (
            <div className={`flex items-center gap-2 pt-1 ${TYPE.body} font-medium text-secondary-700 dark:text-secondary-200`}>
              <Check className="w-4 h-4 shrink-0" />
              <span>Link copied — paste it anywhere to share.</span>
            </div>
          )}

          {/* Fallback only when the clipboard was blocked: show the link to copy manually. */}
          {link && !copied && (
            <div className="pt-1">
              <p className={`mb-1.5 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>Copy this link to share:</p>
              <div className="flex items-center gap-2 rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 px-3 py-2">
                <Link2 className="w-4 h-4 shrink-0 text-secondary-400" />
                <span className={`flex-1 truncate ${TYPE.control} text-secondary-700 dark:text-secondary-200`} title={link}>{link}</span>
                <button onClick={copy} title="Copy link"
                  className={`shrink-0 rounded-md px-2 py-1 ${TYPE.control} font-medium text-white dark:text-secondary-900 bg-secondary-900 dark:bg-white hover:bg-black dark:hover:bg-secondary-100`}>
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareMediaModal;
